/* eslint-disable no-shadow */
import {
  React,
  useRef,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import ImageGallery from 'react-image-gallery';
import { ButtonComponent } from 'component/Button';
import { ReactComponent as CalendarSvg } from 'assets/images/icons/Calendar.svg';
import { ReactComponent as MapSvg } from 'assets/images/icons/map1.svg';
import { ReactComponent as PeopleSvg } from 'assets/images/icons/People.svg';
import { ReactComponent as PhotoSvg } from 'assets/images/icons/Photo.svg';
import { axiosWrapper } from 'utils/Helper';
import { getPhotoByName } from 'api/ImagesApi';
import { engEvidencesType } from 'consts/Evidences';
import { ReportImageDetailInfo } from './ReportImageDetailInfo';

const ImagePreview = (props) => (
  <img
    src={props.url}
    alt={props.url}
    onClick={() => {
      props.openImageModal(props.index);
    }}
  />
);

export const MapDonorPopup = (props) => {
  const [mapPopupData, setMapPopupData] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [clickedReport, setClickedReport] = useState(null);

  const clickHandlerRef = useRef(null);
  const generalClickHandlerRef = useRef(null);
  const turnPointsHandlerRef = useRef(null);
  const ntsHandlerRef = useRef(null);
  const isMountedRef = useRef(true);
  const photosRef = useRef([]);
  photosRef.current = photos;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!props.map) return;

    if (generalClickHandlerRef.current) {
      props.map.off('click', generalClickHandlerRef.current);
    }
    if (turnPointsHandlerRef.current) {
      props.map.off('click', 'turnPointsPolygon-layer', turnPointsHandlerRef.current);
    }
    if (ntsHandlerRef.current) {
      props.map.off('click', 'ntsSource-layer', ntsHandlerRef.current);
    }

    const newClickHandler = (e) => {
      props.setLoader(true);
      setPhotos([]);
      setMapPopupData(e.features[0].properties);
    };

    const generalClickHandler = () => {
      setMapModalOpen(false);
      setClickedReport(null);
    };

    const turnPointsHandler = (e) => {
      newClickHandler(e);
      setClickedReport('ha');
    };

    const ntsHandler = (e) => {
      newClickHandler(e);
      setClickedReport('nts');
    };

    props.map.on('click', generalClickHandler);
    props.map.on('click', 'turnPointsPolygon-layer', turnPointsHandler);
    props.map.on('click', 'ntsSource-layer', ntsHandler);

    generalClickHandlerRef.current = generalClickHandler;
    turnPointsHandlerRef.current = turnPointsHandler;
    ntsHandlerRef.current = ntsHandler;
    clickHandlerRef.current = newClickHandler;

    return () => {
      props.map.off('click', generalClickHandler);
      props.map.off('click', 'turnPointsPolygon-layer', turnPointsHandler);
      props.map.off('click', 'ntsSource-layer', ntsHandler);
    };
  }, [props.map]);

  useEffect(() => {
    return () => {
      photosRef.current.forEach((p) => { if (p.data_url) URL.revokeObjectURL(p.data_url); });
    };
  }, []);

  const getPhoto = (reportId, imageFile) => {
    if (!imageFile) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      axiosWrapper(() => {
        getPhotoByName(reportId, imageFile)
          .then((res) => {
            if (!isMountedRef.current) return resolve();
            if (res.status === 200) {
              const image = new File([res.data], imageFile, {
                type: 'image/jpeg',
              });

              setPhotos((prevPhotos) => [
                ...prevPhotos,
                {
                  file: image,
                  data_url: URL.createObjectURL(res.data),
                  name: imageFile.file_name,
                },
              ]);

              resolve();
            } else {
              reject(new Error('Photo request failed'));
            }
          })
          .catch((err) => {
            if (!isMountedRef.current) return reject(err);
            toast.error(
              `Photo with file name ${imageFile} not loaded`,
            );
            props.setLoader(false);
            reject(err);
          });
      });
    });
  };

  useEffect(() => {
    if (!mapPopupData) return;
    const photos = mapPopupData?.photos ? JSON.parse(mapPopupData?.photos) : [];
    if (!photos.length) {
      setMapModalOpen(true);
      props.setLoader(false);
      return;
    }
    photos.forEach((imageName) => {
      getPhoto(mapPopupData.id, imageName)
        .then(() => {
          if (!isMountedRef.current) return;
          props.setLoader(false);
          setMapModalOpen(true);
        })
        .catch((err) => {
          if (!isMountedRef.current) return;
          console.error(err);
          props.setLoader(false);
          setMapModalOpen(true);
        });
    });
  }, [mapPopupData]);

  const openImageModal = (index) => {
    setCurrentImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setCurrentImageIndex(null);
    setIsImageModalOpen(false);
  };

  return (
    mapModalOpen && (
      <div className="mapbox-popup donor-popup-info">
        <div className="header">Report description</div>
        <div className="body">
          <div className="flex-group">
            <CalendarSvg />
            <span className="double-text">
              <span>{mapPopupData.report_date || '-'}</span>
              <span>Report date</span>
            </span>
          </div>
          <div className="flex-group">
            <PeopleSvg />
            <span className="double-text">
              <span>{mapPopupData.team_id || '-'}</span>
              <span>Team</span>
            </span>
          </div>
          {clickedReport === 'ha' && (
            <>
              <div className="flex-group">
                <MapSvg />
                <span className="double-text">
                  <span>{mapPopupData.location || '-'}</span>
                  <span>Location</span>
                </span>
              </div>
              <div className="flex-group">
                <PhotoSvg />
                <span className="double-text">
                  <span>
                    {JSON.parse(mapPopupData.mines)?.map((mine, index, array) => (
                      <>
                        <b>{engEvidencesType[mine] || '-'}</b>
                        {index < array.length - 1 && ', '}
                      </>
                    ))}
                  </span>
                  <span>Type of evidence</span>
                </span>
              </div>
              <div className="flex-column-group flex-center">
                {photos.length > 0 && (
                  <>
                    <span className="images-preview">
                      {photos.map((image, index) => {
                        if (index < 5) {
                          return (
                            <ImagePreview
                              openImageModal={openImageModal}
                              index={index}
                              url={image.data_url}
                              key={index}
                            />
                          );
                        }
                        return null;
                      })}
                      {photos.length > 4
                        && photos.length - 5 > 0
                        && (
                          <span
                            className="flex-center text"
                            style={{ 'margin-left': '40px' }}
                          >
                            +
                            {' '}
                            {photos.length - 5}
                            {' '}
                            photos more

                          </span>
                        )}

                    </span>
                    <div className="flex-center">
                      <ButtonComponent
                        className="outline"
                        label="Open image gallery"
                        handleButton={() => openImageModal(0)}
                      />
                    </div>
                  </>
                )}

                <Modal
                  isOpen={isImageModalOpen}
                  onRequestClose={closeImageModal}
                  className="react-inner-modal-class"
                  overlayClassName="react-modal-class"
                >
                  {currentImageIndex !== null && (
                    <div className="image-gallery-wrapper">
                      <ImageGallery
                        items={photos.map((image) => ({
                          original: image.data_url,
                          thumbnail: image.data_url,
                          thumbnailHeight: '60px',
                          thumbnailWidth: '60px',
                        }))}
                        startIndex={currentImageIndex}
                        onSlide={(index) => setCurrentImageIndex(index)}
                      />
                      <ReportImageDetailInfo
                        mapPopupData={mapPopupData}
                        photos={photos}
                        currentImageIndex={currentImageIndex}
                        closeImageModal={closeImageModal}
                      />
                    </div>
                  )}
                </Modal>
              </div>
            </>
          )}
        </div>
        <div className="footer">
          <div className="text-center">
            <ButtonComponent
              className="danger"
              label="Close"
              handleButton={() => {
                setMapModalOpen(false);
              }}
            />
          </div>
        </div>
      </div>
    )
  );
};
